// src/modules/app-views/welcome/pages/Init/modals/FirstSubscriptionModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Row, Col, Card } from 'antd';
import { useRef, useEffect, useCallback, useState } from 'react';
import { BankOutlined } from '@ant-design/icons';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleCountry from 'components/SimpleComponents/SimpleCountry';
import SimpleEntityUid, { getEntityUidFieldName } from 'components/SimpleComponents/SimpleEntityUid';
import translateX from 'utils/translateX';
import { SUBSCRIPTION_PROFILE_OPTIONS } from 'modules/app-views/account/service';
import app from 'configs/appResolver';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { returnOnlyNumbers } from 'utils/helpers';
import { isValidCnpj, isValidNipc, isValidEin } from 'utils/validation';
import appService from 'services/appService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { } = {};

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step1Content = ({ docStateRef, defaultCountryCode }) => {

  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const { formTmpState, form } = useSimpleFormContext();

  // 3. LOCAL VARIABLES I
  const presetProfile = app.PRESET?.PROFILE;

  // Track the country code the user explicitly selected via SimpleCountry.
  // userCountryRef is the source of truth for useEffect restoration.
  // displayCountryCode (useState) drives which tax-ID component renders -
  // decoupled from Form.useWatch so CNPJ state side-effects never cause a flash.
  const userCountryRef = useRef(form.getFieldValue('countryCode') || defaultCountryCode);
  const [displayCountryCode, setDisplayCountryCode] = useState(
    form.getFieldValue('countryCode') || defaultCountryCode,
  );

  const handleCountryChange = useCallback(val => {
    // SimpleCountry passes a plain uppercase string via onCountryChange.
    const cc = typeof val === 'string' ? val : val?.value || val?.key || null;
    if (cc && typeof cc === 'string') {
      userCountryRef.current = cc;
      setDisplayCountryCode(cc);
    }
  }, []);

  const formCountryCode = displayCountryCode;
  const docFieldName = getEntityUidFieldName(formCountryCode);

  // Keep docStateRef in sync so onNext can read the latest search state
  if (docStateRef) {
    docStateRef.current = formTmpState?.cnpj?.[docFieldName] || null;
  }

  // 5. EFFECTS
  // After every CNPJ state change (searching/found/failed), restore countryCode in the
  // form if it was reset as a side effect of handleCnpjBlur or handleExternalApiSearch.
  // handleCnpjBlur triggers a second CNPJ lookup via addressService.getCnpjData and
  // calls setFormCnpjState({ searching: true }) before the first update is flushed by
  // React, which can corrupt the form store for this field.
  useEffect(() => {
    const target = userCountryRef.current;
    if (!target || typeof target !== 'string') return;
    const formValue = form.getFieldValue('countryCode');
    if (formValue !== target) {
      form.setFieldsValue({ countryCode: target });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formTmpState?.cnpj, form]);

  // 8. UI
  const cardTitle = (
    <div className="text-muted">
      <BankOutlined style={{ marginRight: 8 }} />
      {translateX('organization')}
    </div>
  );

  return (
    <div style={{ paddingTop: 8 }}>
      <Card title={cardTitle} className="no-shadow">
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <SimpleCountry name="countryCode" label="country" required onCountryChange={handleCountryChange} />
          </Col>
          <Col xs={24} md={12}>
            <SimpleInput
              name="organization"
              label="organization_name"
              required
              placeholder="enter_organization_name"
            />
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          {!presetProfile && (
            <Col xs={24} md={12}>
              <SimpleSelect
                name="profile"
                label="profile"
                required
                options={SUBSCRIPTION_PROFILE_OPTIONS}
              />
            </Col>
          )}
          <Col xs={24} md={presetProfile ? 24 : 12}>
            <SimpleEntityUid
              countryCode={formCountryCode}
              label={docFieldName}
              required
              searchCnpjByApi
              showApiInfo
              silentSearch
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const stepOrganization = ({ docStateRef, countryCode: defaultCountryCode = 'BR' } = {}) => ({
  stepTitle: 'organization',
  stepTooltip: 'enter_organization_data',
  content: () => <Step1Content docStateRef={docStateRef} defaultCountryCode={defaultCountryCode} />,
  validationFields: app.PRESET?.PROFILE
    ? ['organization', 'countryCode', 'cnpj', 'nipc', 'ein', 'uid']
    : ['organization', 'countryCode', 'profile', 'cnpj', 'nipc', 'ein', 'uid'],
  onEnter: ({ goFocus }) => {
    setTimeout(() => goFocus?.('countryCode'), 100);
  },
  onNext: async ({ form, goFocus }) => {
    const formCountryCode = form.getFieldValue('countryCode') || defaultCountryCode;
    const docFieldName = getEntityUidFieldName(formCountryCode);
    const DOC_DOM_ID = docFieldName;

    const docField = form.getFieldValue(docFieldName);
    const docStr = typeof docField === 'object' ? docField?.value : docField;
    const cleanDoc = returnOnlyNumbers(docStr || '');

    if (formCountryCode === 'PT') {
      if (!isValidNipc(cleanDoc)) { goFocus?.(DOC_DOM_ID); return false; }

      // Wait if search is in progress, but never block on API failure (user can fill manually)
      const state = docStateRef?.current;
      if (state?.searching) await waitForDocSearch(docStateRef, cleanDoc);
      return;
    }

    if (formCountryCode === 'US') {
      if (!isValidEin(cleanDoc)) { goFocus?.(DOC_DOM_ID); return false; }
      return;
    }

    if (formCountryCode === 'BR') {
      if (!isValidCnpj(cleanDoc)) { goFocus?.(DOC_DOM_ID); return false; }

      let state = docStateRef?.current;
      if (state?.searching) state = await waitForDocSearch(docStateRef, cleanDoc);
      if (state?.lastSearchFound === cleanDoc) {
        if (state?.apiFailure) return notifyAndFocusDoc(goFocus, DOC_DOM_ID);
        return;
      }

      document.getElementById(DOC_DOM_ID)?.blur();
      await new Promise(r => setTimeout(r, 250));
      state = await waitForDocSearch(docStateRef, cleanDoc);
      if (state?.lastSearchFound === cleanDoc && !state?.apiFailure) return;
      return notifyAndFocusDoc(goFocus, DOC_DOM_ID);
    }

    // Other countries: 'uid' field - form required validation covers it
    return;
  },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default stepOrganization;

// *******************************************************************************
// HELPERS
// *******************************************************************************

async function waitForDocSearch(docStateRef, cleanDoc) {
  const POLL_MS = 200;
  const TIMEOUT_MS = 20000;
  let waited = 0;

  while (waited < TIMEOUT_MS) {
    await new Promise(r => setTimeout(r, POLL_MS));
    waited += POLL_MS;
    const s = docStateRef?.current;
    if (!s?.searching && s?.lastSearchFound === cleanDoc) return s;
  }

  return docStateRef?.current || null;
}

function notifyAndFocusDoc(goFocus, domId, docType = 'cnpj') {
  appService.notification({
    flag: 'warning',
    title: docType === 'nipc' ? 'nipc_not_found_title' : 'cnpj_not_found_title',
    description: docType === 'nipc' ? 'nipc_api_error_description' : 'cnpj_not_found_description',
  });
  goFocus?.(domId);
  return false;
}
